import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackSignupEvent } from '@/helpers/analytics';
import { getOnboardingGuest, markOnboardingGuestEnrolled } from '@/helpers/onboardingGuest';
import { attachOnboardingSessionId } from '@/helpers/onboardingSession';
import { navigateToError } from '@/helpers/navigateToError';
import { isSignupMinorState } from '@/helpers/signupMinorState';
import {
  createInitialSignupForm,
  isSignupFormValid,
  validateSignupField,
} from '@/Pages/Signup/hooks/useSignupValidation';
import { isAdult } from '@/Pages/Signup/utils/age';
import { applyCpfMask } from '@/Pages/Signup/utils/cpfMask';
import { normalizeSignupFormData } from '@/Pages/Signup/utils/normalizeSignupForm';
import { successNavigationOptions } from '@/Pages/Success/types/successLocationState';
import { api, ApiError } from '@/basedata/apiCall';
import type { SignupFormData } from '@/types/signup';

const FALLBACK_NEXT_STEP = '/success';
const PARENTAL_CONSENT_PATH = '/parental-consent';
// Acompanhantes pendentes: a tela do grupo vive em /invite (banner de
// biometria pronta + cadastro dos adicionais logo abaixo).
const COMPANIONS_PATH = '/invite';
const SIGNUP_PATH = '/signup';

// Modo evento: pré-preenche o cadastro com o perfil identificado no /identify
// (nome/e-mail vindos da lista de convidados + CPF já digitado).
function createPrefilledSignupForm(): SignupFormData {
  const base = createInitialSignupForm();
  const guest = getOnboardingGuest();
  if (guest === null) {
    return base;
  }
  return {
    ...base,
    fullName: guest.fullName || base.fullName,
    email: guest.email ?? base.email,
    birthDate: guest.birthDate ?? base.birthDate,
    nationality: guest.nationality ?? base.nationality,
    document: guest.documentDigits ? applyCpfMask(guest.documentDigits) : base.document,
  };
}

export type UseSignupFormResult = {
  form: SignupFormData;
  fieldErrors: Partial<Record<keyof SignupFormData, string>>;
  touched: Partial<Record<keyof SignupFormData, boolean>>;
  isSubmitEnabled: boolean;
  isSubmitting: boolean;
  setField: (field: keyof SignupFormData, value: string) => void;
  blurField: (field: keyof SignupFormData) => void;
  submit: () => Promise<void>;
};

export function useSignupForm(): UseSignupFormResult {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState<SignupFormData>(() =>
    isSignupMinorState(location.state) ? location.state : createPrefilledSignupForm(),
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof SignupFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof SignupFormData, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubmitEnabled = isSignupFormValid(form);

  const setField = useCallback((field: keyof SignupFormData, value: string) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'nationality' && value !== current.nationality) {
        setFieldErrors((errors) => {
          if (!errors.document) {
            return errors;
          }
          const { document: _document, ...rest } = errors;
          return rest;
        });
      }
      return next;
    });
  }, []);

  const blurField = useCallback((field: keyof SignupFormData) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setForm((current) => {
      const error = validateSignupField(field, current);
      setFieldErrors((errors) => {
        if (!error) {
          const { [field]: _removed, ...rest } = errors;
          return rest;
        }
        return { ...errors, [field]: error };
      });
      return current;
    });
  }, []);

  const submit = useCallback(async () => {
    if (!isSignupFormValid(form)) {
      const fields: (keyof SignupFormData)[] = [
        'fullName',
        'email',
        'birthDate',
        'nationality',
        'document',
      ];
      const nextTouched = fields.reduce<Partial<Record<keyof SignupFormData, boolean>>>(
        (accumulator, field) => ({ ...accumulator, [field]: true }),
        {},
      );
      const nextErrors = fields.reduce<Partial<Record<keyof SignupFormData, string>>>(
        (accumulator, field) => {
          const error = validateSignupField(field, form);
          if (error) {
            return { ...accumulator, [field]: error };
          }
          return accumulator;
        },
        {},
      );
      setTouched(nextTouched);
      setFieldErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);

    void trackSignupEvent({ name: 'signup_submit_click' });

    const normalizedForm = normalizeSignupFormData(form);

    if (!isAdult(normalizedForm.birthDate)) {
      void trackSignupEvent({ name: 'signup_minor_redirect' });
      navigate(PARENTAL_CONSENT_PATH, { state: normalizedForm });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await api.signup.submit(
        attachOnboardingSessionId(normalizedForm),
      );
      // Biometria cadastrada: sela o estado ANTES de navegar, senão voltar no
      // histórico devolve a pessoa para a selfie (e cobra outro liveness).
      markOnboardingGuestEnrolled(response.enrolledAt ?? null);
      // Modo evento: TITULAR com acompanhantes pendentes captura antes do
      // sucesso; dependente (role companion, via link único) vai direto ao fim.
      const onboardingGuest = getOnboardingGuest();
      const pendingCompanions =
        onboardingGuest?.role === 'companion' ? 0 : (onboardingGuest?.companions.pending ?? 0);
      const nextStep =
        pendingCompanions > 0
          ? COMPANIONS_PATH
          : response.nextStep.trim() || FALLBACK_NEXT_STEP;
      void trackSignupEvent({
        name: 'signup_submit_success',
        params: { next_step: nextStep },
      });
      navigate(nextStep, successNavigationOptions);
    } catch (error) {
      void trackSignupEvent({ name: 'signup_submit_error' });
      navigateToError(navigate, {
        error,
        originPath: SIGNUP_PATH,
        originState: form,
        ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, navigate]);

  return {
    form,
    fieldErrors,
    touched,
    isSubmitEnabled,
    isSubmitting,
    setField,
    blurField,
    submit,
  };
}
